<template>
  <div v-if="searchResult.total" class="search-result">
    <div class="resumes-title">
      <!-- mobile -->
      <div class="name-workspaceType">
        {{ $t('dashboard.filters.name') }}
        <br />
        / {{ $t('dashboard.filters.repertory') }}
      </div>

      <div class="name">{{ $t('dashboard.filters.name') }}</div>

      <!-- mobile -->
      <div class="id-job">{{ $t('dashboard.filters.labels') }} / {{ $t('dashboard.filters.jobTitle') }}</div>
      <!-- desktop -->
      <div class="id">{{ $t('dashboard.filters.labels') }}</div>
      <div class="job">{{ $t('dashboard.filters.jobTitle') }}</div>

      <div class="workspaceType">{{ $t('dashboard.filters.repertory') }}</div>
      <div class="date">{{ $t('dashboard.filters.date') }}</div>
    </div>

    <div ref="resumes-container" class="resumes-container">
      <div
        v-for="resume in searchResultItems"
        :key="resume.id"
        class="line"
        :class="{ sharedHover: isSharedIcon === resume.id }"
      >
        <div class="edit-button">
          <div
            v-clickout.stop="() => (editMenu === resume.id ? (editMenu = null) : false)"
            class="more-icon"
            @click.stop="openEditMenu(resume.id)"
          >
            <More />
            <EditPopin
              v-if="editMenu === resume.id"
              :resume="resume"
              :workspace="resume.workspaceType"
              @share="share"
              @update-labels="updateLabels"
              @move-resumes="moveResumes"
              @duplicate="duplicate"
              @remove="remove"
              @unshared="unshared"
            />
          </div>
        </div>

        <router-link :to="{ name: 'editor', params: { id: resume.id } }" class="item-line">
          <div class="image">
            <div :class="!resume.candidate.logoLink ? colorPaletteLogoSearch(resume) : ''">
              <img v-if="resume.candidate.logoLink" :src="resume.candidate.logoLink" alt="candidate picture" />
              <p v-else>{{ resume.candidate.firstname[0] }}</p>
            </div>
          </div>

          <div class="info">
            <div class="name-workspaceType info-item">
              <p class="name-text ellipsis">
                {{ `${resume.candidate.firstname.toLowerCase()} ${resume.candidate.lastname.toLowerCase()}` }}
              </p>
              <span class="ellipsis" :class="getRepertoryClass(resume.workspaceType)">{{
                getRepertory(resume.workspaceType)
              }}</span>
            </div>

            <div class="name info-item">
              <div class="name-text ellipsis">
                {{ `${resume.candidate.firstname.toLowerCase()} ${resume.candidate.lastname.toLowerCase()}` }}
              </div>
              <div class="shared-popin-container">
                <Men
                  v-if="resume.isShared"
                  class="isShared-icon"
                  @mouseover="openIsShared(resume.id)"
                  @mouseleave="
                    isSharedIcon = null;
                    sharedInformation = { collabos: {} };
                    fetch = false;
                  "
                />
                <ShareTooltip
                  v-if="isSharedIcon === resume.id && sharedInformation.collabos.length"
                  :workspace="resume.workspaceType"
                  :shared-information="sharedInformation"
                  :collaborators="collaborators"
                />
              </div>
            </div>

            <!-- mobile -->
            <div class="id-job info-item ellipsis">
              <p class="identifier ellipsis">{{ resume.labels.map(label => label.value).join(', ') }}</p>
              <p class="job-title ellipsis">{{ cleanJobTitle(resume.candidate.jobTitle) }}</p>
            </div>
            <!-- desktop -->
            <div class="id info-item labels ellipsis">
              <span v-if="resume.labels?.length > 0" class="inline-label">{{ resume.labels[0].value }}</span>
              <span v-if="resume.labels?.length > 1" class="inline-label" :style="{ flexShrink: 0 }"
                >+{{ resume.labels.length - 1 }}</span
              >
              <div v-if="resume.labels?.length > 1" class="hover">
                <span v-for="label in resume.labels" :key="label.id">
                  {{ label.value }}
                </span>
              </div>
            </div>
            <div class="job info-item ellipsis">
              {{ cleanJobTitle(resume.candidate.jobTitle) }}
            </div>

            <div class="workspaceType info-item">
              <span :class="getRepertoryClass(resume.workspaceType)">{{ getRepertory(resume.workspaceType) }}</span>
            </div>

            <div class="date info-item ellipsis">
              {{ formatDate(resume.updatedAt) }}
            </div>
          </div>
        </router-link>
      </div>
    </div>
  </div>

  <div v-else class="empty-result">
    <p>{{ $t('dashboard.searchResult.noResult') }}</p>
  </div>
</template>

<script>
import { formatDate } from '@/libraries/formatter';
import { mapGetters } from 'vuex';

import Mixin from './dashboard-mixin';
import ShareTooltip from './ShareTooltip';
import EditPopin from './EditPopin';

export default {
  name: 'SearchResult',
  components: {
    ShareTooltip,
    EditPopin,
  },
  mixins: [Mixin],
  emits: ['scroll'],
  data() {
    return {
      formatDate,
      colorTable: [],
    };
  },
  computed: {
    ...mapGetters(['searchResult', 'user']),
    searchResultItems() {
      const items = this.searchResult.items;
      return [...new Map(items.map(item => [item.id, item])).values()];
    },
  },
  watch: {
    'searchResult.total'() {
      this.colorTable = [];
      this.$nextTick(() => {
        const container = this.$refs['resumes-container'];
        if (container) {
          container.onscroll = () => {
            this.$emit('scroll', container);
          };
        }
      });
    },
  },
  methods: {
    getRepertory(workspaceType) {
      if (workspaceType === 1 && this.user && this.user.organization) return this.user.organization.name;
      else if (workspaceType === 2) {
        return this.$t('dashboard.workspaceGroup.personal');
      } else return this.$t('dashboard.workspaceGroup.shared');
    },
    getRepertoryClass(workspaceType) {
      if (workspaceType === 1) return 'organization';
      else if (workspaceType === 2) return 'personal';
      else return 'shared';
    },
    colorPaletteLogoSearch(resume) {
      const colorPalette = ['grey', 'blue', 'green', 'light-green', 'purple', 'red', 'yellow', 'orange'];
      const currentColor = colorPalette[Math.floor(Date.parse(resume.createdAt) % 8)];
      const groupColor = this.colorTable.find(r => r.groupId === resume.groupId);
      if (groupColor) return groupColor.color;
      else {
        this.colorTable.push({ groupId: resume.groupId, color: currentColor });
        return currentColor;
      }
    },
    cleanJobTitle(jobTitle) {
      const div = document.createElement('div');
      div.innerHTML = jobTitle;
      return div.textContent || div.innerText || '';
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive/dashboard.scss';

.search-result {
  border-radius: 7px;
  box-shadow: 0 1px 7px 0 #dddddd;
  margin-bottom: 30px;
  padding-bottom: 10px;
  background-color: colors.$white-color;
}

.id-job,
.name-workspaceType {
  display: none;
}

.resumes-title {
  cursor: default;
  box-sizing: border-box;
  padding: 0 28px 0 130px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 42px;
  margin-bottom: 10px;
  background-color: colors.$background-color;
  border-radius: 7px 7px 0 0;
  div {
    max-width: 210px;
    width: 22%;
    font-family: Lato, sans-serif;
    font-size: 11px;
    font-weight: bold;
    line-height: 13px;
    color: colors.$light-text-color;

    &:last-child {
      width: 12%;
    }
  }
}

.line {
  cursor: pointer;
  box-sizing: border-box;
  margin: 0 28px;
  display: flex;
  align-items: center;
  background-color: colors.$white-color;
  &:hover {
    background-color: colors.$hover-background-color;
    &.sharedHover {
      background-color: colors.$white-color;
      .item-line {
        color: colors.$text-color !important;
        .job,
        .date {
          font-weight: 300;
        }
      }
    }
  }
  &:not(:last-child) {
    border-bottom: solid 1px #d0d0d0;
  }
  .edit-button:hover {
    background-color: colors.$white-color;
    .more-icon {
      background-color: colors.$light-blue-color;
      color: colors.$white-color;
    }
    & + a {
      background-color: colors.$white-color;
    }
  }
  .edit-button {
    display: flex;
    align-items: center;
    width: 52px;
    height: 42px;
    .more-icon {
      position: relative;
      left: 5px;
      height: 31px;
      width: 31px;
      border-radius: 50%;
      color: colors.$light-blue-color;
      &:hover {
        background-color: colors.$light-blue-color;
        color: colors.$white-color;
      }
    }
  }
  .item-line {
    cursor: pointer;
    display: flex;
    align-items: center;
    width: 100%;
    height: 42px;
    &:hover {
      border-radius: 2px;
      color: colors.$hover-text-color;
      div {
        font-weight: 700;
      }
    }
  }
  .info {
    display: flex;
    flex-grow: 1;
    align-items: center;
  }
  .workspaceType {
    span {
      box-sizing: border-box;
      border-radius: 3px;
      padding: 5px;
      color: colors.$white-color;
      font-size: 11px;
      font-weight: 400;

      &.organization {
        background-color: #ffbca2;
      }
      &.personal {
        background-color: #89bd9e;
      }
      &.shared {
        background-color: #8b1e3f;
      }
    }
  }
  .image {
    justify-content: center;
    width: 52px;
    text-transform: uppercase;
    .grey {
      background: #384547;
    }
    .blue {
      background: #416d85;
    }
    .green {
      background: #40a798;
    }
    .light-green {
      background: #a3c9c7;
    }
    .purple {
      background: #947b89;
    }
    .red {
      background: #c72c41;
    }
    .yellow {
      background: #d1962e;
    }
    .orange {
      background: #ffa96a;
    }

    div {
      border: 1px solid colors.$lightest-grey-color;
      height: 31px;
      width: 31px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      font-weight: 700;
      color: colors.$white-color;
      overflow: hidden;
    }
    img {
      background-size: cover;
      width: 100%;
    }
  }
  .info-item {
    padding: 0 20px 0 0;
    box-sizing: border-box;
    max-width: 210px;
    width: 22%;
    flex-shrink: 0;

    &:last-child {
      width: 12%;
    }
  }
  .name {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-family: Lato, sans-serif;
    font-weight: 700;
    line-height: 17px;
    text-transform: capitalize;

    .name-text {
      max-width: calc(100% - 50px);
    }
    .isShared-icon {
      &:hover {
        color: colors.$light-blue-color !important;
      }
    }
    .shared-popin-container {
      position: relative;
      width: 42px;
      height: 42px;
    }
  }
  .id {
    font-size: 11px;
    font-family: Lato, sans-serif;
    font-weight: 700;
    line-height: 13px;
  }

  .labels {
    font-size: 11px;
    font-family: Lato, sans-serif;
    font-weight: 700;
    color: black;
    display: flex;
    gap: 4px;
    position: relative;
    overflow: visible;

    .inline-label {
      padding: 5px;
      border: colors.$black-color 1px solid;
      border-radius: 3px;
      background-color: colors.$white-color;
      min-width: 0;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    &:hover .hover,
    .hover:hover {
      opacity: 1;
      z-index: 999;
    }

    .hover {
      position: absolute;
      top: 30px;

      display: flex;
      flex-direction: column;
      gap: 8px;

      width: calc(100% - 48px);
      max-height: 128px;
      padding: 8px 12px;

      overflow-y: auto;

      opacity: 0;
      border: #121a2e 1px solid;
      border-radius: 3px;
      background-color: #fefefe;
      transition: opacity 0.2s ease-in-out 0.3s;

      > span {
        white-space: wrap;
      }
    }
  }

  .job,
  .date {
    font-size: 11px;
    font-family: Lato, sans-serif;
    font-weight: 300;
    line-height: 13px;
  }
}

.empty-result {
  background-color: colors.$white-color;
  height: 200px;
  width: 100%;
  border-radius: 7px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 7px 0 #dddddd;
  p {
    font-size: 18px;
    line-height: 25px;
    font-style: italic;
    font-weight: 300;
  }
}
</style>
